import React, { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AdminRoutes';
import { useApi, formatNumber } from '../hooks/useApi';

const NAV = [
  { group: 'Overview', items: [
    { to: '/admin', end: true, icon: 'fa-gauge-high', label: 'Dashboard' },
    { to: '/admin/traffic', icon: 'fa-chart-mixed', label: 'Traffic & channels' },
  ] },
  { group: 'People', items: [
    { to: '/admin/enquiries', icon: 'fa-inbox', label: 'Enquiries', badge: 'new_count' },
    { to: '/admin/applications', icon: 'fa-graduation-cap', label: 'Applications', badge: 'submitted_count' },
    { to: '/admin/subscribers', icon: 'fa-envelope', label: 'Subscribers' },
  ] },
  { group: 'Manage', items: [
    { to: '/admin/tracks', icon: 'fa-list-check', label: 'Internship tracks' },
    { to: '/admin/settings', icon: 'fa-gear', label: 'Settings' },
  ] },
];

const TITLES = {
  '/admin': 'Dashboard',
  '/admin/traffic': 'Traffic & channels',
  '/admin/enquiries': 'Enquiries',
  '/admin/applications': 'Internship applications',
  '/admin/subscribers': 'Newsletter subscribers',
  '/admin/tracks': 'Internship tracks',
  '/admin/settings': 'Settings',
};

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [railOpen, setRailOpen] = useState(false);

  // Unread counts for the rail badges. Cheap queries — one row each.
  const { meta: enquiryMeta } = useApi('/api/admin/contacts',
    { limit: 1, status: 'new' }, { refreshMs: 120000 });
  const { meta: appMeta } = useApi('/api/admin/applications',
    { limit: 1, status: 'submitted' }, { refreshMs: 120000 });

  const counts = {
    new_count: enquiryMeta?.pagination?.total,
    submitted_count: appMeta?.pagination?.total,
  };

  const signOut = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  const initials = (user?.name || '?')
    .split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="spa-shell">
      <nav className={`spa-rail${railOpen ? ' is-open' : ''}`} aria-label="Console sections">
        <div className="spa-rail__brand">
          <div className="spa-rail__name">Starparth</div>
          <div className="spa-rail__sub">Console</div>
        </div>

        <div className="spa-rail__nav">
          {NAV.map((section) => (
            <div key={section.group}>
              <div className="spa-rail__group">{section.group}</div>
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => `spa-navlink${isActive ? ' is-active' : ''}`}
                  onClick={() => setRailOpen(false)}
                >
                  <i className={`fa-solid ${item.icon}`} aria-hidden="true" />
                  {item.label}
                  {item.badge && counts[item.badge] > 0 && (
                    <span className="spa-navlink__count">{formatNumber(counts[item.badge])}</span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </div>

        <div className="spa-rail__foot">
          <div className="spa-who">
            <div className="spa-who__avatar" aria-hidden="true">{initials}</div>
            <div style={{ minWidth: 0 }}>
              <div className="spa-who__name">{user?.name}</div>
              <div className="spa-who__role">{user?.role}</div>
            </div>
          </div>
          <button
            type="button"
            className="spa-btn spa-btn--sm"
            style={{ width: '100%', justifyContent: 'center' }}
            onClick={signOut}
          >
            <i className="fa-solid fa-arrow-right-from-bracket" aria-hidden="true" /> Sign out
          </button>
        </div>
      </nav>

      {/* Tapping outside closes the rail on small screens. */}
      {railOpen && (
        <div className="spa-scrim" style={{ zIndex: 35 }} onClick={() => setRailOpen(false)} aria-hidden="true" />
      )}

      <div className="spa-main">
        <header className="spa-topbar">
          <button
            type="button"
            className="spa-burger"
            onClick={() => setRailOpen((v) => !v)}
            aria-label="Toggle navigation"
            aria-expanded={railOpen}
          >
            <i className="fa-solid fa-bars" aria-hidden="true" />
          </button>

          <h1 className="spa-topbar__title">{TITLES[pathname] || 'Console'}</h1>

          <div className="spa-topbar__spacer" />

          <a
            className="spa-btn spa-btn--sm"
            href="/"
            target="_blank"
            rel="noreferrer"
          >
            <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
            View site
          </a>
        </header>

        <main className="spa-page">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
